import { isEmpty } from 'lodash-es';
import { nanoid } from 'nanoid';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { useCommonStore } from '~/common/stores/common.store';
import { useFormTemplateDetailStore } from '~/forms/store/form-template-detail.store';
import { useProjectManagementStore } from '~/project-management/store/pm.store';
import { useTerraStore } from '~/terra/store/terra.store';

export const useNaavixStore = defineStore('naavix', {
  state: () => ({
    flags: {
      is_agent_setup: false,
      is_agent_ready: false,
      is_agent_setting_up: false,
      is_agent_setup_failed: '',
    },
    are_messages_loading: false,
    active_chat_id: nanoid(),
    agent_data_updated_at: null,
    messages: [],
    all_chats: [],
    artifacts_map: {},
    is_explicit_function_call_active: false,
  }),
  getters: {
    get_naavix_agent() {
      const common_store = useCommonStore();
      const naavix_config = common_store.is_development
        ? JSON.parse(localStorage.getItem('NAAVIX_CONFIG') || 'null')
        : null;
      const terra_store = useTerraStore();
      const project_management_store = useProjectManagementStore();
      const form_template_detail_store = useFormTemplateDetailStore();
      if (!isEmpty(terra_store.container) && this.$router.currentRoute.value.name === 'terra-viewer') {
        return {
          module: 'terra',
          resource_id: naavix_config?.resource_id ? naavix_config.resource_id : terra_store.container.uid,
          resource_type: naavix_config?.resource_type ? naavix_config.resource_type : 'container',
          is_enabled: terra_store.container?.properties?.enable_naavix_ai,
        };
      }
      else if (!isEmpty(project_management_store?.active_schedule) && this.$router.currentRoute.value.name === 'schedule-details' && project_management_store?.active_schedule?.is_published) {
        return {
          module: 'pm',
          resource_id: naavix_config?.resource_id ? naavix_config.resource_id : project_management_store.active_schedule.uid,
          resource_type: naavix_config?.resource_type ? naavix_config.resource_type : 'schedule',
          is_enabled: project_management_store.active_schedule?.naavix?.enabled,
        };
      }
      else if (!isEmpty(form_template_detail_store?.form_template_detail) && this.$router.currentRoute.value?.matched?.[0]?.name === 'fam') {
        return {
          module: 'forms',
          form_name: form_template_detail_store.form_template_detail.name,
          resource_id: naavix_config?.resource_id ? naavix_config.resource_id : form_template_detail_store.form_template_detail.uid,
          resource_type: naavix_config?.resource_type ? naavix_config.resource_type : 'template_retriever',
          is_enabled: form_template_detail_store.form_template_detail?.naavix?.enabled,
        };
      }
      return null;
    },
  },
  actions: {
    async set_chats() {
      const { data: sessions_list } = await this.$services.ai.get_sessions({
        agent_name: this.get_naavix_agent.module,
        query: {
          resource_id: this.get_naavix_agent.resource_id,
          resource_type: this.get_naavix_agent.resource_type,
        },
      });
      this.all_chats = sessions_list.sessions;
    },
    async set_chat(chat_id) {
      this.active_chat_id = chat_id;
      this.are_messages_loading = true;
      this.messages = [];
      let last_role = null;
      const messages = [];

      const { data: session_details } = await this.$services.ai.get_session({
        agent_name: this.get_naavix_agent.module,
        session_uuid: chat_id,
        query: {
          resource_id: this.get_naavix_agent.resource_id,
          resource_type: this.get_naavix_agent.resource_type,
        },
      });

      session_details.events.forEach((event) => {
        if (event.role !== last_role) {
          const current_message = {
            id: nanoid(),
            sender: event.role === 'user' ? 'user' : 'assistant',
            message: event.role === 'user' ? event.content : '',
            ...(event.role !== 'user' && {
              function_calls: [],
              function_responses: [],
            }),
          };
          messages.push(current_message);
          last_role = event.role;
        }

        if (event.role !== 'user') {
          if (event.event === 'message') {
            messages[messages.length - 1].message += event.content;
          }
          else if (event.event === 'function_call') {
            messages[messages.length - 1].message = '';
            messages[messages.length - 1].function_calls.push(event.content.function_call);
          }
          else if (event.event === 'function_response') {
            messages[messages.length - 1].function_responses.push(event.content.function_response);
          }
        }
      });
      if (this.active_chat_id === chat_id) {
        this.messages = messages;
        this.are_messages_loading = false;
      }
    },
    async is_agent_data_present() {
      try {
        const { data } = await this.$services.ai.get({
          url: `/agents/${this.get_naavix_agent.module}/check-data`,
          query: {
            resource_id: this.get_naavix_agent.resource_id,
            resource_type: this.get_naavix_agent.resource_type,
          },
          toast: false,
        });
        this.agent_data_updated_at = data.last_updated;
        return true;
      }
      catch (error) {
        logger.error(error);
        return false;
      }
    },
    async initialize_module_data() {
      if (!this.get_naavix_agent)
        return;
      this.flags.is_agent_setting_up = true;
      try {
        if (this.get_naavix_agent.module === 'terra') {
          // Terra watches on flags.value.is_agent_setting_up and initializes the agent by calling either module_initialization_complete or module_initialization_failed.
          return;
        }
        else if (this.get_naavix_agent.module === 'forms') {
          await this.$services.forms.get({
            url: `forms/${this.get_naavix_agent.resource_id}/generate-ai-data`,
            toast: false,
          });
        }
        else if (this.get_naavix_agent.module === 'pm') {
          await this.$services.project_management.post({
            url: `/schedules/${this.get_naavix_agent.resource_id}/write-handler`,
            toast: false,
          });
        }
        this.module_initialization_complete();
      }
      catch {
        this.module_initialization_failed();
      }
    },
    async module_initialization_complete() {
      const is_agent_setup = await this.is_agent_data_present();
      if (is_agent_setup) {
        this.flags.is_agent_ready = true;
        this.flags.is_agent_setting_up = false;
        this.flags.is_agent_setup_failed = '';
      }
      else {
        this.module_initialization_failed();
      }
    },
    module_initialization_failed(reason = null) {
      if (this.flags.is_agent_setup) {
        this.$toast({
          title: 'Failed to refresh data',
          text: 'Something went wrong while trying to fetch the latest data. Please try again',
          type: 'error',
        });
      }
      if (!reason)
        reason = 'Unknown error';
      this.flags.is_agent_setup_failed = reason;
      this.flags.is_agent_ready = false;
      this.flags.is_agent_setting_up = false;
    },
  },
});

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useNaavixStore, import.meta.hot));
